import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import router from "../router/index.js";
import createPersistedState from "vuex-persistedstate";
import ridersStore from "./RidersStore";
import trackersStore from "./TrackersStore";
import arrivalsStore from "./ArrivalsStore";
import usersStore from "./UsersStore";
import racesStore from "./RacesStore";

Vue.use(Vuex);

export default new Vuex.Store({
  strict: false,
  plugins: [
    createPersistedState({
      paths: [
        // Ensure you set all of these paths to null on logout
        "auth.logged_in",
        "auth.auth_method",
        "arrivalsStore.selectedControlPoint",
        "usersStore.me",
        "racesStore.selectedRace",
        "racesStore.availableRaces",
      ],
    }),
  ],
  modules: {
    arrivalsStore,
    ridersStore,
    trackersStore,
    usersStore,
    racesStore,
  },
  state: {
    auth: {
      loading: null,
      auth_method: localStorage.getItem("auth_method") || null, // 'legacy' or 'auth0'
      // Legacy JWT tokens
      access_token: "" || localStorage.getItem("access"),
      refresh_token: "" || localStorage.getItem("refresh"),
      // Auth0 integration
      auth0_user: null,
      auth0_token: localStorage.getItem("auth0_token") || null,
      // General auth state
      logged_in: false,
    },
    baseURL: "",
    defaultProtocol: "",
    apiRoutes: {
      token: "token/",
      refresh: "token/refresh/",
      forgot_password: "accounts/forgot_password",
      arrivals: "api/v1/arrivals/",
      pairs: "api/v1/pairs/",
      riderDetail: "api/v1/riders/",
      races: "api/v1/races/",
      controlPoints: "api/v1/controlpoints/",
      trackers: "api/v1/trackers/",
      organisation: "api/v1/organisations/",
      me: "api/v1/users/me/",
      changePassword: "api/v1/users/change_password/",
    },
    messages: [],
    messageID: 0,
  },
  mutations: {
    setAuthMethod(state, method) {
      state.auth.auth_method = method;
      localStorage.setItem("auth_method", method);
    },
    set_access(state, access) {
      state.auth.access_token = access;
      localStorage.setItem("access", access);
      state.auth.loading = 2;
      state.auth.logged_in = true;
      state.auth.auth_method = 'legacy';
      localStorage.setItem("auth_method", "legacy");
      // Set authorization header for legacy token
      axios.defaults.headers.common["Authorization"] = "Bearer " + access;
    },
    set_refresh(state, refresh) {
      state.auth.refresh_token = refresh;
      localStorage.setItem("refresh", refresh);
    },
    // Auth0 mutations
    setAuth0User(state, user) {
      state.auth.auth0_user = user;
    },
    setAuth0Token(state, token) {
      state.auth.auth0_token = token;
      if (token) {
        localStorage.setItem("auth0_token", token);
        state.auth.auth_method = 'auth0';
        localStorage.setItem("auth_method", "auth0");
        // Set authorization header for Auth0 token
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
      }
    },
    setAuthenticated(state, isAuthenticated) {
      state.auth.logged_in = isAuthenticated;
    },
    auth_logout(state) {
      state.auth.access_token = "";
      state.auth.refresh_token = "";
      state.auth.logged_in = false;
      state.auth.auth_method = null;
      // Clear Auth0 state
      state.auth.auth0_user = null;
      state.auth.auth0_token = null;
      delete axios.defaults.headers.common["Authorization"];
      // Clear all localStorage items
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      localStorage.removeItem("auth0_token");
      localStorage.removeItem("auth_method");
      router.push("/login").catch(() => {});
    },
    setMessage(state, payload) {
      state.messageID += 1;
      state.messages.push({
        status: payload.status,
        message: payload.message,
        id: state.messageID,
      });
    },
    killMessage(state) {
      state.messages.shift();
    },
    baseURL(state, url) {
      state.baseURL = url;
    },
    defaultProtocol(state, protocol) {
      state.defaultProtocol = protocol;
    },
  },
  actions: {
    newMessage({ commit }, payload) {
      commit("setMessage", payload);
      setTimeout(() => {
        commit("killMessage");
      }, 3000);
    },
    setBaseURL({ commit }) {
      return new Promise((resolve) => {
        if (process.env.NODE_ENV == "development") {
          commit("baseURL", "http://127.0.0.1:8000/");
          commit("defaultProtocol", "http://");
        } else {
          commit("baseURL", process.env.VUE_APP_BASE_API_URL);
        }
        resolve();
      });
    },
    auth_refresh({ commit, dispatch }) {
      if (this.state.auth.logged_in && this.state.auth.loading != 1) {
        return new Promise((resolve, reject) => {
          axios({
            url: this.state.baseURL + this.state.apiRoutes.refresh,
            data: {
              refresh: this.state.auth.refresh_token,
            },
            method: "POST",
          })
            .then((resp) => {
              commit("set_access", resp.data.access);
              resolve(resp.data.access);
            })
            .catch((err) => {
              commit("authError");
              dispatch("logout");
              reject(err);
            });
        });
      } else if (this.state.auth.loading == 1) {
        return Promise.resolve();
      } else {
        return Promise.reject();
      }
    },
    async logout({ commit, state }) {
      // Check if using Auth0 BEFORE clearing state
      const isAuth0 = state.auth.auth_method === 'auth0';

      // Clear Vuex state
      commit("usersStore/setMe", null);
      commit("arrivalsStore/setControlPoint", null);
      commit("racesStore/setSelectedRace", null);
      commit("racesStore/setAvailableRaces", null);
      commit("auth_logout");

      // If using Auth0, also logout from Auth0
      if (isAuth0) {
        try {
          await this._vm.$auth0.logout();
        } catch (error) {
          console.error('Auth0 logout error:', error);
        }
      }
    },
    async auth0Login({ commit, dispatch }) {
      try {
        await this._vm.$auth0.login();
      } catch (error) {
        console.error('Auth0 login error:', error);
        dispatch('newMessage', {
          status: 'danger',
          message: 'Login failed. Please try again.'
        });
      }
    },
    async initAuth0({ commit, dispatch }) {
      try {
        const isAuthenticated = await this._vm.$auth0.init();

        if (isAuthenticated) {
          const user = await this._vm.$auth0.getUser();
          const token = await this._vm.$auth0.getToken();

          commit('setAuth0User', user);
          commit('setAuth0Token', token);
          commit('setAuthenticated', true);

          // Fetch user data from your API using Auth0 token
          try {
            const me = await dispatch('usersStore/getMe');

            // Set up user data like the legacy login
            commit('usersStore/setMe', me);
            if (me.available_modules) {
              commit('usersStore/setAvailableModules', me.available_modules);
            }
            if (me.races) {
              commit('racesStore/setAvailableRaces', me.races);
              if (!this.state.racesStore.selectedRace && me.races.length > 0) {
                commit('racesStore/setSelectedRace', me.races[0]);
              }
            }

            // Redirect to races page if we're on login or home page
            if (router.currentRoute.path === '/login' || router.currentRoute.path === '/') {
              router.push({ name: 'races' });
            }
          } catch (error) {
            console.error('Failed to fetch user data:', error);
            // Still keep user logged in even if getMe fails
          }
        }

        return isAuthenticated;
      } catch (error) {
        console.error('Auth0 initialization error:', error);
        return false;
      }
    },
    login({ rootState, commit, dispatch }, user) {
      return new Promise((resolve) => {
        axios({
          url: this.state.baseURL + this.state.apiRoutes.token,
          data: user,
          method: "POST",
        })
          .then((resp) => {
            commit("set_access", resp.data.access);
            commit("set_refresh", resp.data.refresh);
            dispatch("usersStore/getMe").then((user) => {
              commit("usersStore/setMe", user);
              commit("usersStore/setAvailableModules", user.available_modules);
              commit("racesStore/setAvailableRaces", user.races);
              if (!rootState.racesStore.selectedRace) {
                commit("racesStore/setSelectedRace", user.races[0]);
              }
            });
            resolve(resp);
          })
          .catch((err) => {
            console.error(err);
          });
      });
    },
  },
});
